/**
 * Licensed Materials - Property of IBM
 * IBM Cognos Products: BI Cloud (C) Copyright IBM Corp. 2018, 2021
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */

define([
	'jquery',
	'underscore',
	'../../app/nls/StringResources',
	'doT',
	'text!../../common/templates/TranslationOverlayTemplate.html',
	'../../lib/@waca/dashboard-common/dist/ui/dialogs/ConfirmationDialog',
	'../util/ButtonHideHelper',
	'../../lib/@waca/core-client/js/core-client/utils/Utils'
], function($, _, StringResources, dot, TranslationOverlay, ConfirmationDialog, ButtonHideHelper, Utils) {
	'use strict';

	class TranslationService {

		/**
		 * @param options.glassContext - glass context
		 * @param options.eventRouter - event router
		 */
		constructor(options) {
			_.extend(this, options);
			this.blacklistCSVAttributes = ['titleHtml'];
			this.propsStoredAsHTML = ['content'];
			this.localizedItems = {};
			this.dashboardApi.getFeature('Properties').registerProvider(this);

			this._glass = this.dashboardApi.getFeature('Glass');
			this.registeredHandlers = [];
			const featureChecker = this.dashboardApi.getGlassCoreSvc('.FeatureChecker');
			this.isPropertiesV2Enabled = featureChecker && featureChecker.checkValue && featureChecker.checkValue('dashboard', 'PropertiesV2', 'enabled');
		}

		/**
		 * @param options.view
		 * @param options.model
		 */
		registerView(options) {
			this.localizedItems[options.model.id] = options;

			if (this.isInTranslationMode()) {
				this._startModelTranslationMode(options.model, options.view, this._getTranslationModeLocale());
			}
		}

		/**
		 * @param options.model
		 */
		deregisterView(id) {
			if (this.isInTranslationMode()) {
				this._endModelTranslationMode(this.localizedItems[id].model, this.localizedItems[id].view, true);
			}
			delete this.localizedItems[id];
		}

		/**
		 * @param options.defaultLocale
		 */
		initialize(options) {
			this.$el = options.view;
			this.boardModel = options.boardModel;
			this.defaultLocale = options.boardModel.properties.defaultLocale;

			options.boardModel.properties.on('change:translationModeLocale', this._onChangeTranslationMode, this);
			options.boardModel.properties.on('change:defaultLocale', this._onChangeDefaultLocale, this);
			options.boardModel.properties.on('change:switchLanguageInfo', this._onChangeToLocale, this);

			this._contentLocalesPromise = this.dashboardApi.getFeature('UserProfile').getAvailableContentLocales()
				.then((locales) => {
					this._contentLocales = locales;
				});

			// [perf] we started this in the ctor...
			return this._contentLocalesPromise;
		}

		/**
		 * Returns an array of radio button property specs to show in the select a language slideout
		 */
		getLanguageRadioItems() {
			var languageList = [];
			var selectedLanguages = this.getSelectedLanguages();
			if(this.getDefaultLanguage() === 'Default') {
				// If a defaultLocale hasn't been chosen, 'Default' will be one of the selected languages
				// we need to remove any selected languages so any default may be chosen
				selectedLanguages = [];
			}
			_.each(this._contentLocales, function (label, value) {
				if (selectedLanguages.indexOf(value) === -1) {
					languageList.push({
						value: value,
						label: label
					});
				}
			});
			return languageList;
		}

		getContentLocales() {
			return this._contentLocales;
		}

		getLanguageName(languageId) {
			return this._contentLocales[languageId];
		}

		_onChangeTranslationMode() {
			var translationModeLocale = this._getTranslationModeLocale();
			if (!translationModeLocale) {
				this._endTranslationMode();
			} else {
				this._startTranslationMode(translationModeLocale);
			}
		}

		_getTranslationModeLocale() {
			return this.boardModel.properties.get('translationModeLocale');
		}

		isInTranslationMode() {
			return this.translationModeOn;
		}

		/**
		 * Use a property in the model to trigger the translation mode so that
		 * we can take advantage of the undo/redo stack
		 */
		startTranslationMode(selectedLanguage) {
			this.boardModel.setTranslationLocale(selectedLanguage);
		}

		endTranslationMode() {
			this.boardModel.setTranslationLocale(null);
		}

		_startTranslationMode(selectedLanguage) {
			var translationOverlay = dot.template(TranslationOverlay)({
				'text': StringResources.get('multilingualDashboardTranslationMode', {
					currentLanguage: this.getLanguageName(selectedLanguage)
				}),
				'buttonText': StringResources.get('multilingualDashboardTranslationDone')
			});
			this.$el.append(translationOverlay);
			this.$el.find('.translate-button').click(this.endTranslationMode.bind(this));
			this.translationModeOn = true;
			this.translationModeLanguage = selectedLanguage;
			this.eventRouter.trigger('properties:refreshPane');
			ButtonHideHelper.changeMode(this.glassContext, 'translation');

			this.numTranslationsLeft = 0;
			this.showingMissingTranslationCount = false;
			for (let itemId in this.localizedItems) {
				let { model, view } = this.localizedItems[itemId];
				this._startModelTranslationMode(model, view, selectedLanguage);
			}
		}

		_startModelTranslationMode(model, view, selectedLanguage) {
			const multilingualAttributesInfo = model.getMultilingualAttributes();
			const missingTranslationMultilingualAttributesInfo = [];
			const localizedItem = this.localizedItems[model.id];

			if (localizedItem.enterTranslationMode) {
				localizedItem.enterTranslationMode();
			}

			multilingualAttributesInfo.forEach((multilingualAttributeInfo) => {
				const { propertyName, propertyParentModel, multilingualProperty } = multilingualAttributeInfo;
				multilingualProperty.startTranslation(selectedLanguage);

				if(this.dashboardApi && propertyParentModel.multilingual) {
					const content = this.dashboardApi.getCanvas().getContent(model.id);
					content.on('change:property:' + multilingualAttributeInfo.propertyParentModel.name, this._onPropertyChange.bind(this, model.id, model, multilingualAttributeInfo.propertyParentModel.name));
				} else {
					const handler = propertyParentModel.on('change:' + propertyName, this._onPropertyChange.bind(this, model.id, propertyParentModel, propertyName));
					this.registeredHandlers.push(handler);
				}

				if(Object.keys(multilingualProperty.translationTable).length === 0) {
					return;
				}

				if(multilingualProperty.needsTranslation()) {
					missingTranslationMultilingualAttributesInfo.push(multilingualAttributeInfo);
				} else {
					this._updateView(propertyParentModel, propertyName, this.localizedItems[model.id]);
				}
			});

			if (missingTranslationMultilingualAttributesInfo.length > 0) {
				this._appendTranslationIcon(view, missingTranslationMultilingualAttributesInfo);
			}
		}

		_onPropertyChange(modelId, parentModel, propertyName) {
			const multilingualAttributeInfo = parentModel.getMultilingualAttribute(propertyName);
			if(!multilingualAttributeInfo) {
				return;
			}
			const { multilingualProperty } = multilingualAttributeInfo;
			var view = this.localizedItems[modelId].view;
			if (multilingualProperty.needsTranslation()) {
				this._appendTranslationIcon(view, [multilingualAttributeInfo]);
			} else {
				this._removeTranslationIcon(view, [multilingualAttributeInfo]);
			}
		}

		_endTranslationMode() {
			this.$el.find('div.translate-overlay').remove();

			this.translationModeOn = false;
			this.showingMissingTranslationCount = false;

			for (var itemId in this.localizedItems) {
				var item = this.localizedItems[itemId];
				this._endModelTranslationMode(item.model, item.view, false);
			}

			this.eventRouter.trigger('properties:refreshPane', {
				focusSelector: '.clickablelanguage_' + this.translationModeLanguage
			});
			ButtonHideHelper.changeMode(this.glassContext, 'authoring');
		}

		_endModelTranslationMode(model, view, deregisteringView) {

			const localizedItem = this.localizedItems[model.id];
			if (localizedItem.leaveTranslationMode) {
				localizedItem.leaveTranslationMode();
			}

			this.registeredHandlers.forEach(handler => {
				handler.remove();
			});
			this.registeredHandlers = [];

			const multilingualAttributesInfo = model.getMultilingualAttributes();
			multilingualAttributesInfo.forEach((multilingualAttributeInfo) => {
				const { propertyName, propertyParentModel, multilingualProperty } = multilingualAttributeInfo;

				multilingualProperty.stopTranslation();
				if (!deregisteringView) {
					this._updateView(propertyParentModel, propertyName, this.localizedItems[model.id]);
				}
				if(this.dashboardApi && propertyParentModel.multilingual) {
					const content = this.dashboardApi.getCanvas().getContent(model.id);
					content.off('change:property:' + multilingualAttributeInfo.propertyParentModel.name, this._onPropertyChange.bind(this, model.id, model, multilingualAttributeInfo.propertyParentModel.name));
				}
			});
			this._removeTranslationIcon(view, multilingualAttributesInfo);
		}

		/**
		 * Will cause the view/text to get updated so the correct string (locale) is shown
		 */
		_updateView(mode, propertyName, localizedItem) {
			if (localizedItem.reRender) {
				localizedItem.reRender();
			} else {
				this._triggerTextUpdate(mode, propertyName);
			}
		}

		_triggerTextUpdate(model, propertyName) {
			var obj = {};
			obj[propertyName] = model.get(propertyName); //TODO fix this in Endor with ES6
			model.set(obj, {
				payloadData: {
					forceEvent: true,
					skipUndoRedo: true
				},
				sender: 'TranslationService'
			});
		}

		/**
		 * Adds the translation icon
		 * @param {Object} view - the view that was registered with the translation service
		 * @param {Array} multilingualAttributesInfo - All the information about the multilingual attributes in the model that are missing translations
		 */
		_appendTranslationIcon(view, multilingualAttributesInfo) {
			// We should never append translation icons if we're not in translation mode
			if (!this.isInTranslationMode()) {
				return;
			}
			if(view.addTranslationIcon) {
				this.numTranslationsLeft += view.addTranslationIcon(multilingualAttributesInfo);
			} else {
				this.appendTranslationIcon(view.$el);
				this.numTranslationsLeft++;
			}

			this._displayRemainingMessage();
		}

		appendTranslationIcon($el) {
			if (this.isInTranslationMode()) {
				const icons = this.dashboardApi.getFeature('Icons');
				var $iconContainer = $('<div class="translateIcon"></div>');
				Utils.setIcon($iconContainer, icons.getIcon('warning').id, StringResources.get('multilingualDashboardIconTranslatableArea'));
				$el.append($iconContainer);
			}
		}

		updateRemainingCount(delta) {
			if (delta !== 0) {
				this.showingMissingTranslationCount = true;
			}
			this.numTranslationsLeft += delta;
			this._displayRemainingMessage();
		}

		/**
		 * Adds the translation icon
		 * @param {Object} view - the view that was registered with the translation service
		 * @param {Array} multilingualAttributesInfo - All the information about the multilingual attributes in the model for which we want the icon to be removed
		 */
		_removeTranslationIcon(view, multilingualAttributesInfo) {
			let el = view.$el;
			if (view.removeTranslationIcon) {
				const numRemoved = view.removeTranslationIcon(multilingualAttributesInfo);
				if (numRemoved) {
					this.updateRemainingCount(-numRemoved);
				}
			} else {
				if (this.hasTranslationIcon(el)) {
					this.updateRemainingCount(-1);
					this.removeTranslationIcon(el);
				}
			}
		}

		hasTranslationIcon($el) {
			return $el.find('.translateIcon').length > 0;
		}

		removeTranslationIcon($el) {
			$el.find('.translateIcon').remove();
		}

		_displayRemainingMessage() {
			if(this.numTranslationsLeft >= 0 && this.showingMissingTranslationCount){
				var remainingTranslations;
				if(this.numTranslationsLeft === 1){
					remainingTranslations = StringResources.get('multilingualDashboardRemainingString');
				} else{
					remainingTranslations = StringResources.get('multilingualDashboardRemainingStrings', {
						numTranslationsLeft: this.numTranslationsLeft
					});
				}
				this.$el.find('.translate-text').text(remainingTranslations);
			}
		}

		downloadTranslations(selectedLanguage){
			var localizedStrings = this._getLocalizedStrings(selectedLanguage);
			var filename = 'OriginalStrings_' + selectedLanguage;
			this.dashboardApi.getFeature('CsvExport').export(filename, [localizedStrings]);
		}

		removeTranslations(selectedLanguage) {
			const language = this.getContentLocales()[selectedLanguage];
			const confirmCallback = () => {
				var undoRedoTransactionId = _.uniqueId('removeTranslations');
				for (let itemId in this.localizedItems) {
					let { model } = this.localizedItems[itemId];
					const multilingualAttributesInfo = model.getMultilingualAttributes();
					multilingualAttributesInfo.forEach(({ propertyName, propertyParentModel }) => {
						var payload = {};
						payload[propertyName] = {
							value: null,
							locale: selectedLanguage
						};

						propertyParentModel.set(payload, {
							sender: 'TranslationService',
							payloadData: {
								undoRedoTransactionId: undoRedoTransactionId
							}
						});
					});

					this._onMultilingualPropertyChange(itemId);
				}
				this.eventRouter.trigger('properties:refreshPane', {
					focusSelector: '.r_addLanguage-1'
				});
			};
			const dlg = new ConfirmationDialog({
				title: StringResources.get('multilingualDeleteTitle'),
				message: StringResources.get('multilingualDeleteLanguageWarning', { language: language }),
				confirmCallback: confirmCallback
			});
			dlg.render();
		}

		changeDefaultLanguage(selectedLanguage) {
			this.boardModel.properties.set({
				defaultLocale: selectedLanguage
			});
		}

		/**
		 *
		 * @param {Object} property It should has the following structure:
		 * {
		 *     editor: {
		 *          uiControl: {}
		 *     }
		 * }
		 * @param {MultilingualAttribute Object} multilingualProperty
		 * @returns The passed in property
		 * @description This will end up being called by PropertyUIControlView after the property has been rendered.
		 */
		processMultilingualProperty(property, multilingualProperty){
			if (multilingualProperty) {
				const appendTranslationIcon = function($el) {
					if(multilingualProperty.needsTranslation()) {
						this.appendTranslationIcon($el);
					}
				};
				property.editor.uiControl.appendTranslationIcon = appendTranslationIcon.bind(this);
			}
			return property;
		}

		/**
		 * Currently only needed by LiveWidget to deal with the fact they have 2 model representing the same information
		 */
		_onMultilingualPropertyChange(modelId) {
			const localizedItem = this.localizedItems[modelId];
			if (localizedItem && localizedItem.onMultilingualPropertyChange) {
				localizedItem.onMultilingualPropertyChange();
			}
		}

		_onChangeDefaultLocale() {
			var defaultLocale = this.getDefaultLanguage();
			this.boardModel.setDefaultLocale(defaultLocale);

			for (let itemId in this.localizedItems) {
				let { model } = this.localizedItems[itemId];
				const multilingualAttributesInfo = model.getMultilingualAttributes();
				multilingualAttributesInfo.forEach(({ propertyName, propertyParentModel, multilingualProperty }) => {
					multilingualProperty.setDefaultLocale(defaultLocale);
					this._updateView(propertyParentModel, propertyName, this.localizedItems[itemId]);
				});

				this._onMultilingualPropertyChange(itemId);
			}

			this.eventRouter.trigger('properties:refreshPane', {
				focusSelector: '.clickablelanguage_' + defaultLocale
			});
		}

		_onChangeToLocale() {
			var options = this.boardModel.properties.get('switchLanguageInfo');

			for (let itemId in this.localizedItems) {
				let { model } = this.localizedItems[itemId];
				const multilingualAttributesInfo = model.getMultilingualAttributes();
				multilingualAttributesInfo.forEach(({ propertyName, propertyParentModel, multilingualProperty }) => {
					multilingualProperty.switchLocale(options);
					this._updateView(propertyParentModel, propertyName, this.localizedItems[itemId]);
				});

				this._onMultilingualPropertyChange(itemId);
			}

			this.eventRouter.trigger('properties:refreshPane', {
				focusSelector: '.clickablelanguage_' + options.to
			});
		}

		/**
		 * @param {String} options.from - locale switch from
		 * @param {String} options.to - locale switching to
		 */
		switchLanguage(options) {
			if (options.from === this.getDefaultLanguage()) {
				this.changeDefaultLanguage(options.to);
			} else {
				// Set the property silently so the undo/redo stack will get the correct 'before' value
				this.boardModel.properties.set({
					switchLanguageInfo: {
						from: options.to,
						to: options.from
					}
				}, {
					silent: true,
					payloadData: {
						skipUndoRedo: true
					}
				});
				this.boardModel.properties.set({
					switchLanguageInfo: options
				});
			}
		}

		getDefaultLanguage() {
			return this.boardModel.get('properties').get('defaultLocale');
		}

		_getLocalizedStrings(language) {
			const stringList = [];
			for (let itemId in this.localizedItems) {
				let { model } = this.localizedItems[itemId];

				const multilingualAttributes = model.getMultilingualAttributes();
				multilingualAttributes.forEach(({ propertyName, multilingualProperty }) => {
					if(this.blacklistCSVAttributes.includes(propertyName)) {
						return;
					}

					let value = multilingualProperty.getValue(language);
					if(!value) {
						return;
					}

					if(this.propsStoredAsHTML.includes(propertyName)) {
						let $textNode;
						try {
							$textNode = $(value);
						} catch(error) {
							$textNode = [];
						} finally {
							if($textNode.length) {
								value = $textNode.text();
							}
						}
					}

					value = value.trim();

					stringList.push(value);
				});
			}

			return stringList;
		}

		_addTo(list, values) {
			return _.union(list, Object.keys(values));
		}

		/**
		 * Gets all the locales the dashboard has been translated into
		 */
		getSelectedLanguages() {
			let languageList = [];
			for (let itemId in this.localizedItems) {
				let { model } = this.localizedItems[itemId];
				const multilingualAttributes = model.getMultilingualAttributes();
				multilingualAttributes.forEach(({ multilingualProperty }) => {
					languageList = languageList.concat(multilingualProperty.getLanguages());
				});
			}

			return _.uniq(languageList);
		}

		destroy() {
			if (this.dashboardApi) {
				this.dashboardApi.getFeature('Properties').deregisterProvider(this);
			}
			this.localizedItems = null;
			this._contentLocales = null;
		}

		getPropertyLayoutList() {
			return [{
				id: 'advanced',
				type: 'Section',
				label: StringResources.get('sectionName_advanced'),
				position: 99
			}, {
				id: 'languages',
				type: 'Section',
				label: StringResources.get('multilingualDashboardsLabel'),
				collapsible: false,
				style: 'light'
			}];
		}

		getPropertyList() {
			let propertiesManager = this.dashboardApi.getFeature('propertiesManager');

			if (!propertiesManager) {
				return [];
			}
			if (this.getDefaultLanguage() === 'Default') {
				//Advanced.languages property's selection list is populated using content locale list
				//Hide Advanced.languages property when there are no available content locales
				if(this.getContentLocales()){
					return this._getLanguageSpecWhenNoLocaleSet(propertiesManager);
				}else {
					return [];
				}
			}
			return this._getMultilingualProperties(propertiesManager);
		}

		/**
		 * Language property spec when no language has been selected yet
		 */
		_getLanguageSpecWhenNoLocaleSet(propertiesManager) {
			const defaultLanguage = this.getDefaultLanguage();
			let sectionId;
			if (this.isPropertiesV2Enabled) {
				sectionId = 'advanced';
			} else {
				sectionId = 'general.advanced';
			}
			return [{
				id: 'language_' + defaultLanguage,
				editor: {
					sectionId: sectionId,
					position: 3,
					uiControl: {
						type: 'SingleLineLinks',
						items: [{
							align: 'left',
							items: [{
								type: 'text',
								id: 'text' + defaultLanguage,
								value: StringResources.get('multilingualDashboardsLabel')
							}]
						}, {
							align: 'right',
							items: [{
								type: 'text',
								value: StringResources.get('multilingualDashboardsSetDefault'),
								clickCallback: () => {
									this.launchLanguageSelectSlideout(defaultLanguage, false, propertiesManager);
								}
							}]
						}]
					}
				}
			}];
		}

		_getMultilingualProperties(propertiesManager) {
			const defaultLanguage = this.getDefaultLanguage();
			let sectionId;
			if (this.isPropertiesV2Enabled) {
				sectionId = 'advanced.languages';
			} else {
				sectionId = 'general.advanced.languages';
			}
			const properties = [...this._createLanguageRow(defaultLanguage, sectionId, true)];

			// Rows for all other available languages
			this.getSelectedLanguages().sort((a, b) => {
				return this.getLanguageName(a) > this.getLanguageName(b) ? 1 : -1;
			}).forEach((language) => {
				if (language !== defaultLanguage && language !== 'Default') {
					properties.push(this._createLanguageRow(language, sectionId, false));
				}
			});
			properties.push({
				id: 'addLanguage',
				editor: {
					sectionId: sectionId,
					uiControl: {
						type: 'SingleLineLinks',
						isDisabled: this.translationModeOn,
						items: [{
							align: 'right',
							items: [{
								type: 'text',
								value: StringResources.get('multilingualDashboardsAddLinkText'),
								clickCallback: () => {
									this.launchLanguageSelectSlideout(null, true, propertiesManager);
								}
							}]
						}]
					}
				}
			});

			return properties;
		}

		/**
		 * Creats a SingleLineLink property spec that describes a language
		 */
		_createLanguageRow(locale, sectionId, isDefaultLocale) {
			const languageName = this.getLanguageName(locale);

			return {
				id: 'language_' + locale,
				editor: {
					sectionId: sectionId,
					uiControl: {
						type: 'SingleLineLinks',
						indent: 2,
						disabled: this.translationModeOn,
						items: [{
							align: 'left',
							items: []
						}, {
							align: 'right',
							items: [{
								type: 'text',
								id: 'text' + locale,
								value: isDefaultLocale ? StringResources.get('multilingualDashboardsDefault', { defaultLanguage: languageName }) : languageName
							}, {
								type: 'icon',
								svgIcon: 'common-menuoverflow',
								iconTooltip: StringResources.get('multilingualDashboardIconMore', {
									currentLanguage: languageName
								}),
								clickCallback: (evt) => {
									this.launchLanguageMenu(evt, locale);
								}
							}]
						}]
					}
				}
			};
		}

		/**
		 * When picking a language, we launch an overlay slideout with all the content locales
		 */
		launchLanguageSelectSlideout(selectedLocale, addLanguage, propertiesManager) {
			// ensure properties manager is available
			propertiesManager = propertiesManager || this.dashboardApi.getFeature('propertiesManager');
			propertiesManager.addChild({
				overlay: true,
				label: StringResources.get('multilingualSelectLanguage'),
				content: {
					module: 'dashboard-core/js/dashboard/contentpane/PropertyUIControlView',
					items: [{
						value: StringResources.get('multilingualSelectLanguage'),
						centerLabel: true,
						type: 'Banner',
						backButton: true,
						ariaLabel: StringResources.get('multilingualSelectLanguage')
					}, {
						type: 'RadioButtonGroup',
						name: 'language',
						separator: true,
						ariaLabel: StringResources.get('multilingualSelectLanguage'),
						value: selectedLocale,
						selectOnNavigation: false,
						items: this.getLanguageRadioItems(),
						onChange: (name, newLocale) => {
							propertiesManager.closeChild();
							if (selectedLocale === 'Default') {
								this.changeDefaultLanguage(newLocale);
							} else if (addLanguage) {
								this.startTranslationMode(newLocale);
							} else {
								this.switchLanguage({
									from: selectedLocale,
									to: newLocale
								});
							}
						}
					}]
				}
			});
		}

		/**
		 * User clicked on the ..., so show the menu
		 */
		launchLanguageMenu(evt, locale) {
			let position = {};
			if (evt.pageX === undefined ||
				(evt.gesture && (evt.gesture.center === undefined || evt.gesture.center.pageX === undefined))) {
				position = $(evt.target).offset();
			} else {
				position.left = evt.pageX || evt.gesture.center.pageX;
				position.top = evt.pageY || evt.gesture.center.pageY;
			}

			this._glass.showContextMenu({
				position: {
					pageX: position.left,
					pageY: position.top
				},
				menuId: 'com.ibm.bi.dashboard.languageMenu',
				activeObject: {
					selectedLocale: locale,
					translationService: this,
					layoutBaseView: this
				}
			});
		}
	}

	return TranslationService;
});
